import type {ApolloCache} from '@apollo/client';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client/react';

import {SELLER_COMPANY_TYPES} from '../constants/dictionaries';
import {graphql} from '../gqlApp';
import {useCompanyActivityRefetch} from './activity';
import {GET_COMPANY_OFFER} from './offer';

/**
 * Документы и тонкие обёртки-хуки рядом — как в otveto/apps/web/src/api/*.ts.
 * Хуки useQuery/useMutation вызываются вручную, codegen генерирует только типы.
 */

graphql(`
	fragment CompanyFields on Company {
		id
		type
		status
		wizardStep
		legalForm
		shortName
		fullName
		inn
		kpp
		ogrn
		legalAddress
		actualAddress
		taxSystem
		vatRate
		taxOffice
		directorFullName
		directorPosition
		contactPersonName
		phone
		email
		accountingEmail
		autofilledFields
		createdAt
		updatedAt
	}
`);

/**
 * Банковские реквизиты — отдельным фрагментом, а не полями CompanyFields.
 *
 * Так они не уезжают в список компаний для переключателя в шапке: читать расчётные
 * счета всех компаний пользователя на каждую его отрисовку незачем. На бэкенде это
 * отдельный резолвер по той же причине, и там же в SKS-18 появятся маскирование
 * счёта и аудит чтения — фрагмент спрашивает реквизиты только там, где они нужны.
 */
graphql(`
	fragment CompanyBankFields on Company {
		bankDetails {
			bik
			bankName
			bankCorrAccount
			bankAccount
		}
	}
`);

export const GET_MY_COMPANIES = graphql(`
	query MyCompanies {
		myCompanies {
			...CompanyFields
		}
	}
`);

export const useMyCompanies = () => useQuery(GET_MY_COMPANIES);

/**
 * Продающая ли сторона смотрит экран: селлер или компания со своим складом.
 * undefined — пока не известно.
 *
 * Из списка компаний, а не из карточки компании (SKS-636): список уже загружен
 * шапкой кабинета и отдаётся из кэша. Отдельный запрос карточки оставлял окно,
 * в котором селлер считался «не селлером», — экран успевал отрисоваться не той
 * стороной, а редактор товарного профиля открывался с пустым черновиком.
 */
export function useSellingSide(companyId: string | undefined): boolean | undefined {
	const {data} = useMyCompanies();
	const type = data?.myCompanies.find((item) => item.id === companyId)?.type;

	return type ? SELLER_COMPANY_TYPES.includes(type) : undefined;
}

/**
 * Компании с ролью в каждой — для профиля (SKS-257). Отдельный документ, а не
 * поле в CompanyFields: за myRole бэкенд ходит в БД на каждую компанию, а
 * переключателю в шапке роль не нужна, и платить за неё он не должен.
 */
export const GET_MY_MEMBERSHIPS = graphql(`
	query MyMemberships {
		myCompanies {
			id
			type
			status
			shortName
			fullName
			inn
			myRole
		}
	}
`);

export const useMyMemberships = () => useQuery(GET_MY_MEMBERSHIPS);

export const GET_COMPANY = graphql(`
	query Company($id: ID!) {
		company(id: $id) {
			myRole
			...CompanyFields
			statusReason
			# Чёрный список фулфилмента (SKS-36) — плашка на складах, без основания
			intakeRestrictedByPlatform
			...CompanyBankFields
			# Только в карточке: поле считает полноту заявки вместе с документами,
			# и в списке компаний это был бы лишний обход на каждую строку
			missingForSubmit
			# Баланс юрлица (SKS-14). null — у компании баланса не бывает (не селлер),
			# и карточка баланса не рисуется вовсе
			balance {
				balanceKopecks
				lastEntryAt
			}
		}
	}
`);

export const useCompany = (id: string | undefined) => {
	// Один сигнал компании (SKS-249) обновляет и баланс, и статус: оба приходят этим
	// запросом. Списание за хранение и авто-суспенд идут без действий пользователя
	useCompanyActivityRefetch(id, ['Company']);

	return useQuery(GET_COMPANY, {variables: {id: id ?? ''}, skip: !id});
};

export const CREATE_COMPANY_DRAFT = graphql(`
	mutation CreateCompanyDraft($input: CreateCompanyDraftInput!) {
		createCompanyDraft(input: $input) {
			...CompanyFields
		}
	}
`);

/**
 * Одна учётка — одна сторона рынка (SKS-661): какие компании аккаунт может создать.
 * Отдельным запросом, а не полем GET_ME: за ним бэкенд ходит в БД, а GET_ME шапка
 * запрашивает на каждой странице — платят только мастер и экран приглашения.
 */
export const GET_COMPANY_PARTY = graphql(`
	query CompanyParty {
		me {
			id
			companyParty {
				allowedTypes
				anchor {
					id
					name
					type
				}
			}
		}
	}
`);

export const useCompanyParty = (options?: {skip?: boolean}) => useQuery(GET_COMPANY_PARTY, options);

/*
 * Сторона аккаунта зависит от его компаний, и после создания или удаления черновика
 * старый ответ в кэше врёт: мастер нарисовал бы карточки по нему до ответа сети.
 *
 * refetchQueries здесь не помогает — Apollo перезапрашивает только смонтированные
 * запросы, а черновик удаляют с карточки компании, где мастера нет. Поэтому поле
 * выкидываем из кэша: следующий мастер не найдёт его и дождётся сервера.
 */
const evictCompanyParty = (cache: ApolloCache) => {
	const meId = cache.readQuery({query: GET_COMPANY_PARTY})?.me.id;

	if (meId) {
		cache.evict({id: cache.identify({__typename: 'Me', id: meId}), fieldName: 'companyParty'});
		cache.gc();
	}
};

export const useCreateCompanyDraft = () =>
	useMutation(CREATE_COMPANY_DRAFT, {
		refetchQueries: [GET_MY_COMPANIES],
		update: evictCompanyParty,
	});

export const UPDATE_COMPANY_DRAFT = graphql(`
	mutation UpdateCompanyDraft($input: UpdateCompanyDraftInput!) {
		updateCompanyDraft(input: $input) {
			...CompanyFields
			# Пятый шаг сохраняет реквизиты этой же мутацией — без них в ответе
			# кэш остался бы с прежними значениями
			...CompanyBankFields
		}
	}
`);

export const useUpdateCompanyDraft = () => useMutation(UPDATE_COMPANY_DRAFT);

/**
 * Отправка заявки — она же принятие оферты (SKS-237): договор заключается здесь,
 * а не после модерации. Редакцию и ключ идемпотентности присылает мастер; там,
 * где оферты для аудитории нет, оба поля пустые.
 */
export const SUBMIT_COMPANY = graphql(`
	mutation SubmitCompany($id: ID!, $offerVersionId: ID, $offerIdempotencyKey: String) {
		submitCompany(
			id: $id
			offerVersionId: $offerVersionId
			offerIdempotencyKey: $offerIdempotencyKey
		) {
			...CompanyFields
		}
	}
`);

/**
 * После отправки перезапрашиваем состояние оферты: принятие только что появилось,
 * а на нём висят баннер, точка в меню и карточка компании. Смержить его из ответа
 * нельзя — у CompanyOfferState нет идентификатора, и кэш оставит прежнее значение.
 */
export const useSubmitCompany = (companyId?: string) =>
	useMutation(SUBMIT_COMPANY, {
		refetchQueries: companyId ? [{query: GET_COMPANY_OFFER, variables: {companyId}}] : [],
		/*
		 * Ждём ответа перед переходом: кнопка мастера всё это время в состоянии загрузки,
		 * а без ожидания карточка успевает открыться со старым состоянием оферты — «примете
		 * при отправке заявки» у той, которую только что приняли.
		 */
		awaitRefetchQueries: true,
	});

export const DELETE_COMPANY_DRAFT = graphql(`
	mutation DeleteCompanyDraft($id: ID!) {
		deleteCompanyDraft(id: $id)
	}
`);

/**
 * Удалённая компания могла быть текущей в шапке, поэтому список обновляем
 * запросом, а не правкой кэша: заодно уйдут её склады из связанных запросов.
 */
export const useDeleteCompanyDraft = () =>
	useMutation(DELETE_COMPANY_DRAFT, {
		refetchQueries: [GET_MY_COMPANIES],
		awaitRefetchQueries: true,
		update: evictCompanyParty,
	});

export const SUGGEST_COMPANIES = graphql(`
	query SuggestCompanies($query: String!) {
		suggestCompanies(query: $query) {
			available
			items {
				value
				legalForm
				shortName
				fullName
				inn
				kpp
				ogrn
				legalAddress
				taxOffice
				directorFullName
				directorPosition
				status
				isBranch
			}
		}
	}
`);

/**
 * Подсказки по ИНН или названию. Ленивый запрос: ходить на каждое нажатие клавиши
 * незачем, вызов делает поле после паузы в наборе.
 *
 * Кэш по строке запроса включён специально — пользователь стирает символ и печатает
 * его снова, и повторный поход в DaData здесь только жжёт лимит запросов.
 */
export const useSuggestCompanies = () => useLazyQuery(SUGGEST_COMPANIES);

export const SUGGEST_BANKS = graphql(`
	query SuggestBanks($query: String!) {
		suggestBanks(query: $query) {
			available
			items {
				value
				bic
				name
				correspondentAccount
				city
				status
				isBranch
			}
		}
	}
`);

/** Подсказки по БИК или названию банка — тот же ленивый режим, что и у компаний. */
export const useSuggestBanks = () => useLazyQuery(SUGGEST_BANKS);

export const GET_COMPANY_DOCUMENTS = graphql(`
	query CompanyDocuments($companyId: ID!) {
		companyDocuments(companyId: $companyId) {
			requiredKinds
			items {
				id
				kind
				status
				fileName
				mimeType
				sizeBytes
				rejectionReason
				uploadedAt
			}
		}
	}
`);

/** Документы компании и требования к пакету. */
export const useCompanyDocuments = (companyId: string | undefined) =>
	useQuery(GET_COMPANY_DOCUMENTS, {
		variables: {companyId: companyId ?? ''},
		skip: !companyId,
	});
